// Where the townsfolk's places and the cutscenes' marks are in the valley (phase D1): each place's spots from its
// anchor's frame (a layout placement, a room's door, an NPC's own home door, the square's middle) to world metres and
// a facing; each mark on the ground. Built once from the world mount; pure lookups after.
import type { PlaceSpot, RoutinePlace } from "@rpg/ai";
import type { Placed } from "@rpg/camera";
import type { WorldMount } from "../world";
import type { TownContent } from "./content";

/** An anchor's frame: an origin on the ground plane and a yaw (yaw 0 faces +z; local x maps to (cos yaw, -sin yaw)). */
export interface Frame {
  x: number;
  z: number;
  yaw: number;
}

/** Local (x across, z ahead) to world, as @rpg/camera's local anchors turn. */
export const toWorld = (f: Frame, x: number, z: number): { x: number; z: number } => {
  const c = Math.cos(f.yaw);
  const s = Math.sin(f.yaw);
  return { x: f.x + x * c + z * s, z: f.z - x * s + z * c };
};

const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

export interface TownPlaces {
  /** The routine place `id` for NPC `npc` (its own home for "home"), or null. */
  place(id: string, npc: string): RoutinePlace | null;
  /** A mark (ground height added), or null. */
  mark(id: string): Placed | null;
  /** A room's door frame (+z out of the door), by the room's placement id. */
  door(placement: string): Frame | null;
  frames(): Record<string, Frame>;
}

export function townPlaces(m: WorldMount, c: TownContent): TownPlaces {
  const frames: Record<string, Frame> = {};
  for (const r of m.rooms) {
    const i = r.piece.interior;
    if (!i) continue;
    const cy = Math.cos(r.place.yaw);
    const sy = Math.sin(r.place.yaw);
    const x = r.place.x + i.door.x * cy + i.door.z * sy;
    const z = r.place.z - i.door.x * sy + i.door.z * cy;
    const nx = i.door.nx * cy + i.door.nz * sy;
    const nz = -i.door.nx * sy + i.door.nz * cy;
    frames[`door:${r.id}`] = { x, z, yaw: Math.atan2(nx, nz) };
  }
  for (const p of m.world.placements()) frames[p.id] ??= { x: p.pos[0], z: p.pos[1], yaw: p.yaw ?? 0 };
  const sq = m.world.layout.regions.find((r) => r.id === "square");
  if (sq && sq.shape.kind === "circle") frames["@square"] = { x: sq.shape.x, z: sq.shape.z, yaw: 0 };
  const homeOf = new Map(c.npcs.map((n) => [n.id, n.home]));
  const cache = new Map<string, RoutinePlace | null>();
  const frameOf = (anchor: string, npc: string): Frame | null => {
    if (anchor === "@home") {
      const h = homeOf.get(npc);
      return h ? (frames[`door:${h}`] ?? frames[h] ?? null) : null;
    }
    return frames[anchor] ?? null;
  };
  const build = (id: string, npc: string): RoutinePlace | null => {
    const row = c.places.get(id);
    if (!row) return null;
    const f = frameOf(row.anchor, npc);
    if (!f) return null;
    const world = row.spots.map((s) => toWorld(f, s.x, s.z));
    const cx = world.reduce((a, p) => a + p.x, 0) / world.length;
    const cz = world.reduce((a, p) => a + p.z, 0) / world.length;
    const spots: PlaceSpot[] = row.spots.map((s, k) => {
      const w = world[k]!;
      let yaw: number | undefined;
      if (s.face === "centre" && Math.hypot(cx - w.x, cz - w.z) > 1e-3) yaw = Math.atan2(cx - w.x, cz - w.z);
      else if (s.face === "anchor") yaw = Math.atan2(f.x - w.x, f.z - w.z);
      else if (s.yaw !== undefined) yaw = wrap(f.yaw + s.yaw);
      return yaw === undefined ? { x: w.x, z: w.z } : { x: w.x, z: w.z, yaw };
    });
    const out: RoutinePlace = { id: row.anchor === "@home" ? `home:${npc}` : id, spots };
    if (row.arrive !== undefined) out.arrive = row.arrive;
    if (row.wanderEvery !== undefined) out.wanderEvery = row.wanderEvery;
    if (row.inside) out.inside = true;
    return out;
  };
  return {
    place(id, npc) {
      const row = c.places.get(id);
      const key = row?.anchor === "@home" ? `${id}|${npc}` : id;
      if (!cache.has(key)) cache.set(key, build(id, npc));
      return cache.get(key)!;
    },
    mark(id) {
      const r = c.marks.get(id);
      if (!r) return null;
      return { pos: [r.x, m.world.heightAt(r.x, r.z) + (r.y ?? 0), r.z], yaw: r.yaw };
    },
    door: (p) => frames[`door:${p}`] ?? null,
    frames: () => ({ ...frames }),
  };
}
